import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  // TODO: migrate to @dnd-kit/react (new API with type/accept/group constraints)
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { Column, Table } from '@tanstack/react-table';
import { ArrowDown, ArrowUp, GripVertical } from 'lucide-react';

export const MAX_SORT_COLUMNS = 3;
export default function TableColumnSortingList<TData>({
  table,
}: {
  table: Table<TData>;
}) {
  const allColumns = table
    .getAllColumns()
    .filter((column) => column.getCanSort());
  const sortedColumns = table.getState().sorting;

  const items = (() => {
    const sorted = sortedColumns.map((sort) => {
      const column = allColumns.find((col) => col.id === sort.id);
      return {
        id: sort.id,
        desc: sort.desc,
        column,
      };
    });

    const available = allColumns
      .filter((col) => !sortedColumns.find((sort) => sort.id === col.id))
      .map((column) => ({
        id: column.id,
        desc: false,
        column,
      }));

    return [...sorted, ...available];
  })();

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const activeSortCount = sortedColumns.length;

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={({ active, over }) => {
        if (over && active.id !== over.id) {
          const oldIndex = items.findIndex((item) => item.id === active.id);
          const newIndex = items.findIndex((item) => item.id === over.id);
          const newItems = arrayMove(items, oldIndex, newIndex);

          const newSorting = newItems
            .slice(0, MAX_SORT_COLUMNS)
            .map((item) => ({
              id: item.id,
              desc: item.desc,
            }));

          table.setSorting(newSorting);
        }
      }}
    >
      <SortableContext items={items} strategy={verticalListSortingStrategy}>
        <div className="space-y-1">
          {activeSortCount > 0 && (
            <div className="text-muted-foreground px-4 py-2 text-sm">
              Sorting by
            </div>
          )}
          {items.map((item, index) => (
            <>
              {index === activeSortCount && (
                <div className="text-muted-foreground px-4 py-2 text-sm">
                  Available for sorting
                </div>
              )}
              <DraggableSortItem
                key={item.id}
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                {...(item as any)}
                isActive={index < activeSortCount}
                onToggleDirection={() => {
                  if (index < activeSortCount) {
                    const newSorting = [...sortedColumns];
                    newSorting[index] = {
                      ...newSorting[index],
                      desc: !newSorting[index].desc,
                    };
                    table.setSorting(newSorting);
                  }
                }}
              />
            </>
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
}

function DraggableSortItem<TData>({
  id,
  column,
  desc,
  isActive,
  onToggleDirection,
}: {
  id: string;
  column: Column<TData>;
  desc: boolean;
  isActive: boolean;
  onToggleDirection: () => void;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: transform ? `translate3d(0, ${transform.y}px, 0)` : undefined,
    transition,
  };

  const label =
    typeof column.columnDef.header === 'string'
      ? column.columnDef.header
      : column.id;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn('flex items-center gap-1', isDragging && 'opacity-50')}
    >
      <Button
        variant="ghost"
        className={cn(
          'flex-1 justify-between gap-2',
          !isActive && 'text-muted-foreground',
        )}
        onClick={onToggleDirection}
      >
        <span className="truncate">{label}</span>
        {isActive &&
          (desc ? (
            <ArrowDown className="size-4 shrink-0" />
          ) : (
            <ArrowUp className="size-4 shrink-0" />
          ))}
      </Button>
      <Button
        variant="ghost"
        size="icon"
        {...attributes}
        {...listeners}
        className="touch-none p-1 opacity-50 hover:opacity-100"
      >
        <GripVertical className="size-4" />
      </Button>
    </div>
  );
}
